import { Routes } from '@angular/router';
import { AccountRepository } from './modules/main/repositories/account.repository';
import { CartRepository } from './modules/main/repositories/cart.repository';
import { CashbookRepository } from './modules/main/repositories/cashbook.repository';
import { DocumentRepository } from './modules/main/repositories/document.repository';
import { EndOfDayRepository } from './modules/main/repositories/end-of-day.repository';
import { PaymentRepository } from './modules/main/repositories/payment.repository';
import { ProductRepository } from './modules/main/repositories/product.repository';
import { TransactionRepository } from './modules/main/repositories/transaction.repository';
import { AccountService } from './modules/main/services/account.service';
import { CartCashbookService } from './modules/main/services/cart-cashbook.service';
import { CartService } from './modules/main/services/cart.service';
import { DocumentService } from './modules/main/services/document.service';
import { EndOfDayService } from './modules/main/services/end-of-day.service';
import FavoriteProductService from './modules/main/services/favorite-product.service';
import { PaymentService } from './modules/main/services/payment.service';
import {
  ProductService,
  ReturnableProductService,
} from './modules/main/services/product.service';
import {
  SOFTLINE_WWS_CASH_REGISTER_CATEGORIES_IMG_CACHE,
  SOFTLINE_WWS_CASH_REGISTER_IMG_CACHE,
} from './pos.tokens';
import { posConfigurationResolver } from './resolver/pos-configuration.resolver';
import { createCache } from './utils/create-cache';
import { inject, INJECTOR } from '@angular/core';
import { configuredGuard } from './guards/configured.guard';
import { VirtualKeyboardService } from '@softline/ui-core';
import { TerminalRepository } from './modules/main/repositories/terminal.repository';
import { TerminalInitService } from './modules/main/services/terminal-init.service';
import { terminalCloseGuard } from './guards/terminal-close.guard';
import { ShellService } from '@softapps/core';
import { Configuration } from './modules/config/types/config';
import { SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY } from './pos.const';
import { WorkstationConfigService } from '@softapps/wws/pos-core';
import { UserInfoService } from '@softline/auth';
import { handleRequestError, RemoteConfigResolver } from '@softline/application';
import { ValueCardRepository } from './modules/main/repositories/value-card.repository';
import { ValueCardService } from './modules/main/services/value-card.service';
import { SellOffService } from './modules/main/services/sell-off.service';
import { SellOffRepository } from './modules/main/repositories/sell-off.repository';

export const posRoutes: Routes = [
  {
    path: 'pos',
    canActivate: [
      configuredGuard,
      async () => {
        const injector = inject(INJECTOR);
        const workstationService = inject(WorkstationConfigService);
        const shellService = inject(ShellService);
        const username = inject(UserInfoService).value()?.userName;

        inject(VirtualKeyboardService).enable();

        try {
          const config = await workstationService.load<Configuration>(
            SOFTLINE_CONST_WWS_CASH_REGISTER_CONFIG_KEY
          );
          shellService.setAppearance({
            titleBar: {
              displayUsername: `${config?.name?.kassennummer}, ${username}`,
              displayDateTime: 'datetime',
            },
          });
        } catch (e) {
          handleRequestError(e, injector, { show: false });
          shellService.setAppearance({
            titleBar: {
              displayUsername: true,
              displayDateTime: 'datetime',
            },
          });
        }
      },
    ],
    canDeactivate: [
      () => {
        inject(VirtualKeyboardService).disable();
        inject(ShellService).setAppearance(undefined);
        return true;
      },
      terminalCloseGuard,
    ],
    resolve: {
      configuration: posConfigurationResolver,
      config: RemoteConfigResolver,
    },
    providers: [
      DocumentService,
      DocumentRepository,
      // CART
      { provide: CartRepository, useClass: CartRepository },
      CartService,
      // PRODUCT
      { provide: ProductRepository, useClass: ProductRepository },
      ProductService,
      ReturnableProductService,
      FavoriteProductService,
      // CASHBOOK
      { provide: PaymentRepository, useClass: PaymentRepository },
      PaymentService,
      // CASHBOOK
      { provide: CashbookRepository, useClass: CashbookRepository },
      { provide: TransactionRepository, useClass: TransactionRepository },
      CartCashbookService,
      // ACCOUNT
      { provide: AccountRepository, useClass: AccountRepository },
      AccountService,
      // VOUCHER
      { provide: ValueCardRepository, useClass: ValueCardRepository },
      ValueCardService,
      // END OF DAY
      { provide: EndOfDayRepository, useClass: EndOfDayRepository },
      EndOfDayService,
      TerminalRepository,
      TerminalInitService,
      SellOffService,
      SellOffRepository,
      {
        provide: SOFTLINE_WWS_CASH_REGISTER_IMG_CACHE,
        useFactory: createCache<string>,
      },
      {
        provide: SOFTLINE_WWS_CASH_REGISTER_CATEGORIES_IMG_CACHE,
        useFactory: createCache<string>,
      },
    ],
    loadChildren: () => import('./modules/main/main.routes'),
  },
  {
    path: 'pos-config',
    resolve: {
      config: RemoteConfigResolver,
    },
    loadChildren: () => import('./modules/config/config.routes'),
    providers: [],
  },
];
